import { Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { ThemeProvider } from 'styled-components';

import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';

import Loader from '@src/components/Loader';
import PrivateRoute from '@src/layouts/PrivateRoute';
import PublicRoute from '@src/layouts/PublicRoute';
import routesConfig from '@src/routes/routesConfig';

import { setSnackbar } from './api/axios';
import { UiRoutes } from './lib/constants';
import { getUserPermissiong } from './lib/helper';
import { routesConfigType } from './lib/types';
import { ReduxProps } from './redux/type';
import { customTheme } from './styles/muiTheme';
import { theme } from './styles/theme';

const App = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { userData } = useSelector((state: ReduxProps) => state?.loginReducer.loginState);
  const path = userData?.org?.org_id ? `/${userData?.org?.org_id}` : '';
  const loaderState = useSelector((state: ReduxProps) => state?.loaderReducer);

  useEffect(() => {
    setSnackbar(enqueueSnackbar);
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (loaderState && Array.isArray(loaderState.actionsLoading) && loaderState.actionsLoading.length && !isLoading) {
      setIsLoading(true);
    } else if (loaderState && Array.isArray(loaderState.actionsLoading) && loaderState.actionsLoading.length === 0) {
      setIsLoading(false);
    }
  }, [loaderState]);

  const renderLayout = (config: routesConfigType): JSX.Element => {
    switch (config.layout) {
      case 'private':
        return (
          <Route key={config.path} path={path + config.path} element={<PrivateRoute component={config.component} />} />
        );
      default:
        return <Route key={config.path} path={config.path} element={<PublicRoute component={config.component} />} />;
    }
  };

  const renderRoute = (config: routesConfigType): JSX.Element | undefined => {
    const userPermissions = getUserPermissiong(userData?.role || '') || [];

    if (userPermissions.includes(config.allowedPermission || '') || config.allowedPermission === 'all') {
      return renderLayout(config);
    }

    return undefined;
  };

  return (
    <>
      <SnackbarProvider>
        <Suspense fallback={<Loader />}>
          <MUIThemeProvider theme={customTheme}>
            <ThemeProvider theme={theme}>
              <Router>
                <Routes>
                  {routesConfig.map(config => renderRoute(config))}
                  <Route path='/' element={<Navigate to={UiRoutes.LOGIN} replace />} />
                  <Route path='*' element={<Navigate to={UiRoutes.LOGIN} replace />} />
                </Routes>
              </Router>
            </ThemeProvider>
          </MUIThemeProvider>
        </Suspense>
      </SnackbarProvider>

      {isLoading && <Loader />}
    </>
  );
};

const AppWithSnackbar: React.FC = () => (
  <SnackbarProvider maxSnack={3}>
    <App />
  </SnackbarProvider>
);

export default AppWithSnackbar;
